import { Status } from '@/pages/OrderManagement/OrderRequests/components/cellRenderers/Status'
import { CommentOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import orderRequestIcon from '@assets/CustomIcons/order-request-icon-gradient.svg'
import { addCommasToNumber, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Popover } from 'antd'
import dayjs from 'dayjs'
import moment from 'moment/moment'
import type React from 'react'
import { useMemo } from 'react'

type RequestItem = {
  delivery_window_start: string
  delivery_window_end: string
  requested_delivery_window_start: string
  created_date: string
  site_number: string
  site_name: string
  destinations?: { site_name: string; site_number: string }[]
  status: 'Accepted'
  products: {
    product_id: string
    product_name: string
    island: string
    volume: number
    quantity: number
  }[]
  total_volume: number
}

type OrderRequestPopoverProps = {
  requestItem: RequestItem
  origins: string[]
  acceptBy: string
  note?: {
    content: string
    updated_by: string
    updated_on: string
  }
  timezone?: string
  hoverElement: React.ReactElement
}

export function OrderRequestPopover({
  hoverElement,
  origins,
  acceptBy,
  note,
  timezone,
  requestItem,
}: OrderRequestPopoverProps) {
  if (!requestItem && hoverElement) return hoverElement
  if (!requestItem) return null
  const status = requestItem?.status === 'Accepted' ? 'Acknowledged' : requestItem?.status
  const { formatVolume } = useFormatter()
  const formattedVolume = formatVolume(null, true)
  const sameDays = moment(requestItem.delivery_window_start).isSame(requestItem.delivery_window_end, 'day')

  const orderRequestDestinations = useMemo(() => {
    if (requestItem.destinations && requestItem.destinations.length > 0) {
      return requestItem.destinations.map((d) => `${d.site_number} - ${d.site_name}`)
    }
    return [`${requestItem.site_number} - ${requestItem.site_name}`]
  }, [requestItem])

  function getDeliveryWindowDate() {
    const startDate = formatters.LONG_MONTH_DATE_YEAR(requestItem.delivery_window_start, timezone)
    const startTime = formatters.TIME(requestItem.delivery_window_start, timezone)
    const endDate = formatters.LONG_MONTH_DATE_YEAR(requestItem.delivery_window_end, timezone)
    const endTime = formatters.TIME(requestItem.delivery_window_end, timezone)

    if (sameDays) return `${startDate} at ${startTime} - ${endTime}`
    return `${startDate} at ${startTime} - ${endDate} at ${endTime}`
  }

  const title = (
    <Horizontal verticalCenter>
      <Horizontal>
        <img src={orderRequestIcon} alt='order request icon' style={{ width: '20px' }} className='mr-2' />
        <Texto category='h5'>Order Request</Texto>
      </Horizontal>
      <Vertical flex={1} alignItems='flex-end'>
        <Texto>
          <span style={{ fontWeight: 'bold' }}>Requested:</span>{' '}
          {formatters.DATE_TIME(dayjs(requestItem.created_date), timezone)}
        </Texto>
        {acceptBy && (
          <Texto>
            <span style={{ fontWeight: 'bold' }}>Accept by:</span> {formatters.DATE_TIME(dayjs(acceptBy), timezone)}
          </Texto>
        )}
      </Vertical>
    </Horizontal>
  )

  const aggregatedProducts = useMemo(() => {
    if (!requestItem.products) return []
    return requestItem.products.reduce((acc: RequestItem['products'], product) => {
      const existingProduct = acc.find((p) => p.product_id === product.product_id && p.island === product.island)
      if (existingProduct) {
        existingProduct.quantity += product.quantity
      } else {
        acc.push({ ...product })
      }
      return acc
    }, [])
  }, [requestItem.products])

  const content = (
    <Vertical style={{ width: '425px' }}>
      {note?.content && (
        <Vertical style={{ backgroundColor: 'var(--theme-color-2-dim)', borderRadius: '4px' }} className='mt-2 p-2'>
          <Texto category='h5'>
            <CommentOutlined /> Note
          </Texto>
          <Texto>{note.content}</Texto>
        </Vertical>
      )}
      <Vertical className='my-2'>
        <Texto category='h5'>Status</Texto>
        <Status data={requestItem} status={status} />
      </Vertical>
      <Vertical className='my-2'>
        <Texto category='h5'>Origin</Texto>
        {origins?.filter((o) => !!o).length > 0 ? (
          origins.map((origin) => <Texto key={origin}>{origin}</Texto>)
        ) : (
          <Texto>N/A</Texto>
        )}
      </Vertical>
      <Vertical className='my-2'>
        <Texto category='h5'>Destination Site</Texto>
        {orderRequestDestinations.map((d) => (
          <Texto key={d}>{d}</Texto>
        ))}
      </Vertical>

      <Vertical className='my-2'>
        <Texto category='h5'>Requested Delivery Window</Texto>
        <Texto>{getDeliveryWindowDate()}</Texto>
      </Vertical>

      <Vertical className='my-2'>
        <Texto category='h5'>Requested Products</Texto>
        {aggregatedProducts?.map((product) => (
          <Horizontal justifyContent='space-between' key={product.product_id + product.island} className='mb-1'>
            <Texto>
              {product.product_name} ({product.island})
            </Texto>
            <Texto className='ml-2' textTransform='lowercase'>
              {addCommasToNumber(product.quantity)} {formattedVolume}s
            </Texto>
          </Horizontal>
        ))}
      </Vertical>

      <Horizontal className='my-2' justifyContent='space-between'>
        <Texto category='h5'>Total Requested Volume</Texto>
        <Texto textTransform='lowercase' weight='bold'>
          {addCommasToNumber(requestItem.total_volume)} {formattedVolume}s
        </Texto>
      </Horizontal>
    </Vertical>
  )

  return (
    <Popover title={title} content={content} trigger='hover'>
      {hoverElement}
    </Popover>
  )
}
